import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, effect, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { NavigateToDirective } from 'projects/shared/directives/navigator.directive';
import { ICart, ICartItem } from 'projects/shared/models/interfaces/cart.model';
import { BrowserService } from 'projects/shared/services/browser.service';
import { CartService } from 'projects/shared/services/cart.service';
import { StoreChangesService } from 'projects/shared/services/store-changes.service';
import { LanguageService } from 'projects/shop/src/app/+shared/services/language.service';
import { filter, noop, Subscription, tap } from 'rxjs';
import { CartActions } from './store/cart.actions';
import { SelectCartItemsEntity, SelectCartIDs, SelectCartItems, SelectCartTotalItems, SelectCart, SelectCartQuantity, SelectCartPrice, SelectCartGetSuccessMessage, SelectCartGetFailureMessage, SelectCartUpdateSuccessMessage, SelectCartUpdateFailureMessage } from './store/cart.selectors';
import { Exchange } from 'projects/shared/models/exchange.model';
import { CurrencyCalculator } from 'projects/admin/src/app/shared/helpers/currency-calculator';
import { SelectRegionalCurrency, SelectRegionalLanguage } from '../nav-regional/store/regional-setting.selectors';
import { DesktopToaster } from 'projects/shared/services/desktop-toaster.service';

@Component({
  selector: 'app-nav-cart',
  standalone: true,
  imports: [CommonModule, RouterModule, OverlayPanelModule, TranslateModule],
  templateUrl: './nav-cart.component.html',
  styleUrl: './nav-cart.component.scss'
})
export class NavCartComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('cartOP') cartOP!: OverlayPanel;

  cartQuantity = 0;

  exchanges!: any;

  updatedCart: any[] = [];

  cart$ = this._store.selectSignal(SelectCart);
  cartIds$ = this._store.selectSignal(SelectCartIDs);
  cartItems$ = this._store.selectSignal(SelectCartItems);
  cartEntity$ = this._store.selectSignal(SelectCartItemsEntity);
  cartTotal$ = this._store.selectSignal(SelectCartTotalItems);
  cartTotalQuantity$ = this._store.selectSignal(SelectCartQuantity);
  cartTotalPrice$ = this._store.selectSignal(SelectCartPrice);
  getCartSuccess$ = this._store.selectSignal(SelectCartGetSuccessMessage);
  getCartFailure$ = this._store.selectSignal(SelectCartGetFailureMessage);
  updateCartSuccess$ = this._store.selectSignal(SelectCartUpdateSuccessMessage);
  updateCartFailure$ = this._store.selectSignal(SelectCartUpdateFailureMessage);

  currency$ = this._store.selectSignal(SelectRegionalCurrency);
  language$ = this._store.selectSignal(SelectRegionalLanguage)

  _subscription = new Subscription();
  currencyCalculator = new CurrencyCalculator()

  constructor(
    private _storeChangesService: StoreChangesService,
    private _browserService: BrowserService,
    private _router: Router,
    private _store: Store,
  ) {
  }

  ngOnInit(): void {
    this._store.dispatch(CartActions.initializeCart({ initialized: true }))
    this.getExchanges()
  }

  ngAfterViewInit(): void {
    this._browserService.getWindow()?.addEventListener('scroll', this.closeOnScroll.bind(this), true);
  }

  inCheckoutPage(): boolean {
    return !!this._browserService.getLocation()?.pathname.includes('checkout')
  }

  navigateToCart() {
    this._router.navigate([`/${this.language$()}/settings/cart`])
  }

  navigate(path: string) {
    this.cartOP.hide();
    setTimeout(() => {
      this._router.navigate([`${path}`]);
    }, 50);
  }

  removeItem(item: ICartItem, index: number) {
    this._store.dispatch(CartActions.removeItem({ id: item.product }))
  }

  checkoutPageIsOpen() {
    let open = false;
    this._subscription.add(
      this._router.events.subscribe(
        res => {
          if (res instanceof NavigationEnd) {
            open = res.urlAfterRedirects.includes('checkout');
          }
        }
      )
    )
    return open;
  }

  getExchanges() {
    this._subscription.add(
      this._storeChangesService.getExchange().pipe(
        tap(
          res => {
            this.exchanges = res;
          }
        )
      ).subscribe(noop)
    )
  }

  closeOnScroll(event: Event) {
    const overlayEl = document.querySelector('.p-overlaypanel');
    if (overlayEl && overlayEl.contains(event.target as Node)) return;
    if (this.cartOP?.overlayVisible) {
      this.cartOP.hide();
    }
  }

  getTotalPrice(products: any[]): number {
    return products.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
  }

  getTotalQuantity(products: any[]): number {
    return products.reduce((total, item) => total + item.quantity, 0);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this._browserService.getWindow()?.removeEventListener('scroll', this.closeOnScroll.bind(this), true);
  }

}
